import { CameraAlt, Done, Replay } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { createDisplayLink } from '../../services/helper';
import { fileUpload, signupFileUpload } from '../../services/services';
import VyButton from './VyButton';
import { VyCropper } from './VyCropper';
import VyModal from './VyModal';
import Image from 'next/image';

interface VyWebcamProps {
  open?: boolean;
  anonymousUserUpload?: boolean;
  eventid?: string;
  onChange: (fileId: string | null) => void;
  onClose: () => void;
}

const HEIGHT = 486 * 2;
const WIDTH = 364 * 2;
const VyWebcam = ({ open, anonymousUserUpload = false, eventid, onClose, onChange }: VyWebcamProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<boolean>(false);
  const [value, setValue] = useState<string | null | undefined>(null);

  const streamRef = useRef<MediaStream>();
  const [isCropping, setIsCropping] = useState(false);

  const videoPlayer = useRef<HTMLVideoElement | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);

  function closeStreams() {
    if (videoPlayer.current) {
      videoPlayer.current.srcObject = null;
      videoPlayer.current.pause();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = undefined;
    }
  }

  function handleClose() {
    closeStreams();
    onClose();
  }

  async function processDevices(devices: MediaDeviceInfo[]) {
    for (const device of devices) {
      if (device.kind === 'videoinput' && !streamRef.current) {
        await setDevice(device);
      }
    }
  }

  async function setDevice(device: MediaDeviceInfo) {
    const { deviceId } = device;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { deviceId } });

    streamRef.current = stream;
    if (videoPlayer.current) {
      videoPlayer.current.srcObject = stream;
      videoPlayer.current.play();
    }
  }

  async function initialize() {
    const cameras = await navigator.mediaDevices.enumerateDevices();
    processDevices(cameras);
  }

  const takePhoto = () => {
    const context = canvas.current?.getContext('2d');
    context?.drawImage(videoPlayer.current as any, 0, 0);
    canvas.current?.toBlob(uploadImage);
  };

  function validate() {
    if (value) onChange(value);
    onClose();
  }

  function uploadImage(file: any, afterCrop = false) {
    setValue(null);
    setPreview(true);
    const upload = anonymousUserUpload && eventid ? signupFileUpload(file, eventid) : fileUpload(file);
    if (file) {
      setIsLoading(true);
      upload
        .then(({ data }: any) => {
          setIsLoading(false);
          setValue(data.fileId);
          setIsCropping(!afterCrop);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }

  useEffect(() => {
    return () => closeStreams();
  }, []);

  useEffect(() => {
    if (!preview && videoPlayer) initialize();
  }, [preview]);

  return (
    <VyModal onClose={handleClose} disableDefaultStyling open={open}>
      {() => (
        <div>
          {!preview ? (
            <div className="flex flex-col justify-center">
              <video className="mb-2" ref={videoPlayer} />
              <VyButton showLoadingIcon={isLoading} disabled={isLoading} onClick={() => takePhoto()}>
                <CameraAlt />
              </VyButton>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2">
              {value && !isCropping ? (
                <div className="relative max-h-[60vh] max-w-[60vw] size-96">
                  <Image fill src={createDisplayLink(value)} alt="photo" className="content-center object-contain" />
                </div>
              ) : (
                <CircularProgress />
              )}
              <div className="flex justify-center gap-4">
                <VyButton
                  disabled={isLoading || !value || isCropping}
                  onClick={() => {
                    setValue(null);
                    setPreview(false);
                  }}>
                  <Replay />
                </VyButton>
                <VyButton disabled={isLoading || isCropping} onClick={() => validate()}>
                  <Done />
                </VyButton>
              </div>
            </div>
          )}
          <div hidden>
            <canvas hidden width={WIDTH} height={HEIGHT} ref={canvas} />
          </div>
          <VyCropper
            open={isCropping}
            image={createDisplayLink(value, false, undefined, undefined, undefined, false)}
            onCrop={(image) => {
              setIsCropping(false);
              uploadImage(image, true);
            }}
            onCancel={() => {
              setValue(null), setPreview(false), setIsCropping(false);
            }}
            withRotation={false}
            size={{ width: 480, height: 480 }}
          />
        </div>
      )}
    </VyModal>
  );
};

export default VyWebcam;
